import React from "react";
import { Checkbox, FormControlLabel, FormHelperText, Grid, Typography, WithStyles } from "@material-ui/core";
import { styles } from "./SampleDataView.styles";
import { withStyles } from "@material-ui/styles";
import { FormTextField } from "../../components/FormComponents/FormTextField";
import { createTarget, phonePattern } from "../../../utilities/generalUtils";
import { FormNativeSelect } from "../../components/FormComponents/FormNativeSelect";
import { FormDateField } from "../../components/FormComponents/FormDateField";
import { today } from "../../../utilities/dateTimeUtils";
import { DATE_FORMAT } from "../../../constants/TIME";
import { BlueButton } from "../../components/Buttons/BlueButton";
import { useSelector } from "react-redux";
import { isEuCountry } from "../../../utilities/countriesUtils";
import { findProductByName, findCategoryByName } from "../productOverview/ProductHelper";
import { useParams } from "react-router-dom";
import { selectVisibleCountries } from "../../../selectors/countries/CountriesSelector";

export interface ISampleDataFormProps extends WithStyles<typeof styles> {
    handleChange: any;
    details: any;
    product: any;
    errors: any;
    handleSubmit: any;
    hasNonEditableFields: boolean;
}

const VALIDATION_GENERAL = "Please enter and check all required fields";

const SampleDataFormPure: React.FunctionComponent<ISampleDataFormProps> = ({
    handleChange,
    details,
    product,
    errors,
    handleSubmit,
    hasNonEditableFields,
    classes
}) => {

    const countries = useSelector(state => selectVisibleCountries(state));
    const a7productsInfoFromState = useSelector((state) => state.a7products);
    const sampleProductsInfo = {
        products: a7productsInfoFromState?.products?.filter(product => !product.id.includes("A7")),
        categories: a7productsInfoFromState?.categories?.filter(category => !category.id.includes("A7"))
    };

    const thisCategory = findCategoryByName(sampleProductsInfo?.categories, product?.category);

    const {productIdentifier} = useParams();
    const thisProduct = findProductByName(sampleProductsInfo?.products, productIdentifier);

    const availableProducts = thisProduct?.subProducts?.map(p => ({name: p.productName, id: p.productId, price: p.price}));

    const formIsValid = () => {
        return (!isEuCountry(countries, details.country) || details.vat)
            && details.companyEmail
            && details.firstName
            && details.lastName
            && details.companyName
            && details.street
            && details.postalCode
            && details.city
            && details.country
            && details.subProductId
            && details.desiredEffectiveDate
            && details.confirmLegalEntity
            && details.termsConditions
            && Object.values(errors).every(x => x === "");
    };

    const errorMessage = Object.keys.length > 0 ? Object.values(errors).filter(e => e !== "").join(", ") : VALIDATION_GENERAL;

    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <Typography variant="h3">Order Form Historical Data for Testing Purposes</Typography>
                <Typography variant="body2">This Order Form relates to the General Terms and Conditions to the Data Shop (hereinafter “GTC”)  which govern the licensing of Historical Data in individual Data Packages and which are available for view, download and print under the following link: <a href="https://datashop.deutsche-boerse.com/terms-and-conditions">https://datashop.deutsche-boerse.com/terms-and-conditions</a>.</Typography>
                <br/>
                <Typography variant="body2">Unless defined otherwise in this Order Form, capitalized terms used in this Order Form have the meaning ascribed to them in the GTC.</Typography>
                <br/>
                <Typography variant="body2">The purpose of this Order Form is to stipulate the individual content and scope of Historical Data to be licensed in accordance with the terms and conditions of the GTC. The Contractual Parties hereby agree that unless explicitly agreed otherwise herein the terms and conditions of the GTC shall apply to this Order Form accordingly. In case of any conflict between the GTC and an Order Form, the provisions of the Order Form shall prevail.</Typography>
            </Grid>
            <Grid item xs={12}>
                <Typography variant="h4">Client contact details</Typography>
            </Grid>
            <Grid item xs={2}>
                <FormTextField
                    id="title"
                    onChange={handleChange}
                    value={details.title}
                    label={"Title"}
                />
            </Grid>
            <Grid item xs={4}>
                <FormTextField
                    id="firstName"
                    onChange={handleChange}
                    value={details.firstName}
                    label={"First name"}
                    required
                    disabled={hasNonEditableFields}
                />
            </Grid>
            <Grid item xs={4}>
                <FormTextField
                    id="lastName"
                    onChange={handleChange}
                    value={details.lastName}
                    label={"Last name"}
                    required
                    disabled={hasNonEditableFields}
                />
            </Grid>
            <Grid item xs={4}>
                <FormTextField
                    id="companyName"
                    onChange={handleChange}
                    value={details.companyName}
                    label={"Company name & Legal form"}
                    required
                    disabled={hasNonEditableFields}
                />
            </Grid>
            <Grid item xs={4}>
                <FormTextField
                    id="companyPhone"
                    onChange={handleChange}
                    value={details.companyPhone}
                    label={"Company phone"}
                    pattern={phonePattern}
                />
            </Grid>
            <Grid item xs={4}>
                <FormTextField
                    id="companyEmail"
                    onChange={handleChange}
                    value={details.companyEmail}
                    label={"Company email"}
                    required
                />
            </Grid>
            <Grid item xs={12}>
                <Typography variant="h4">Legal address</Typography>
            </Grid>
            <Grid item xs={4}>
                <FormTextField
                    id="street"
                    onChange={handleChange}
                    value={details.street}
                    label={"Street"}
                    required
                />
            </Grid>
            <Grid item xs={2}>
                <FormTextField
                    id="postalCode"
                    onChange={handleChange}
                    value={details.postalCode}
                    label={"Postal code"}
                    required
                />
            </Grid>
            <Grid item xs={4}>
                <FormTextField
                    id="city"
                    onChange={handleChange}
                    value={details.city}
                    label={"City"}
                    required
                />
            </Grid>
            <Grid item xs={4}>
                <FormNativeSelect label={"Country"} options={countries} onChange={handleChange}
                                  id="country" value={details.country}
                                  required
                                  emptyCaption={"Select country"}
                />
            </Grid>
            <Grid item xs={4}>
                <FormTextField
                    id="vat"
                    onChange={handleChange}
                    value={details.vat}
                    label={"VAT no. (EU only)"}
                />
            </Grid>
            <Grid item xs={12}>
                <Typography variant="h4">Product</Typography>
            </Grid>
            <Grid item xs={4}>
                <FormNativeSelect
                    label={"product"}
                    options={availableProducts}
                    onChange={handleChange}
                    id="subProductId"
                    value={details.subProductId ? availableProducts.find(p => p.id === details.subProductId).name : ""}
                    required
                    emptyCaption={"Select product"}
                />
            </Grid>
            <Grid item xs={8}>
                <Typography variant="body2">
                    {thisCategory?.legalDisclaimer}
                </Typography>
            </Grid>
            <Grid item xs={2}>
                <Typography variant={"h6"}>Desired effective date **</Typography>
                <FormDateField
                    label="Date"
                    variant="filled"
                    disabled={false}
                    placeholder={"Desired effective date"}
                    value={details.desiredEffectiveDate}
                    firstAvailableDate={today().format(DATE_FORMAT)}
                    lastAvailableDate={today().add(3, "months").format(DATE_FORMAT)}
                    onChange={newValue => handleChange(createTarget("desiredEffectiveDate", newValue))}
                />
            </Grid>
            <Grid item xs={4}>
                <Typography variant={"h6"}>Historical Data Date Range</Typography>
                <Typography variant={"body2"} className={classes.topMargin}>Full year 2022</Typography>
            </Grid>

            <Grid container item xs={12} spacing={2}>
                <Grid item xs={12}>
                    <Typography variant={"h6"}>Message</Typography>
                </Grid>
                <Grid item xs={6}>
                    <FormTextField
                        id="message"
                        multiline
                        rows={8}
                        onChange={handleChange}
                        value={details.message}
                        label={"Please name authorised users (first name, last name, email) which shall have access during the trial period."}
                    />
                </Grid>
            </Grid>

            <Grid container item xs={12} spacing={2}>
                <Grid item xs={6}>
                    <Typography variant="h6">License</Typography>
                    <Typography variant="body2" className={classes.topMargin}>Deutsche Börse AG grants a License subject to Sections 4 to 7 of the GTC.</Typography>
                </Grid>
            </Grid>

            <Grid container item xs={12} spacing={2}>
                <Grid item xs={6}>
                    <Typography variant="h6">Trial Period</Typography>
                    <Typography variant="body2" className={classes.topMargin}>The Data Package is provided for Testing Purposes within a trial period of 4 weeks.</Typography>
                </Grid>
            </Grid>

            <Grid item xs={12} className={classes.summary}>
                <Typography variant="h2">Summary</Typography>
            </Grid>
            <Grid container item xs={12}>
                <Grid item xs={6}>
                    <Typography variant="h4">Product</Typography>
                    <Typography
                        variant="body2">{details.subProductId ? availableProducts.find(p => p.id === details.subProductId).name : "–––"}</Typography>
                </Grid>
                {product?.trial
                    ? <Grid item xs={6}>
                        <Typography variant="h4">Trial period</Typography>
                        <Typography
                            variant="body2">{product?.trialPeriodInWeeks} {product?.trialPeriodInWeeks === 1 ? "week" : "weeks"}</Typography>
                    </Grid>
                    : null}
            </Grid>

            <Grid container item xs={12}>
                 <Grid item xs={6}>
                    <Typography variant="h4">Price - if destroyed after Trial period according to Sec.6 b) of the GTC</Typography>
                    <Typography variant="body2">0 EUR - Free Trial</Typography>
                </Grid>
                <Grid item xs={6}>
                    <Typography variant="h4">Price - if retained after Trial period according to Sec.6 a) of the GTC</Typography>
                    <Typography variant="body2">{details.subProductId ? availableProducts?.find(p => p.id === details.subProductId).price : "–––"} EUR</Typography>
                </Grid>
            </Grid>

            <Grid container item xs={12} className={classes.checkboxes}>
                <Grid item xs={12}>
                    <FormControlLabel
                        control={<Checkbox checked={details.termsConditions !== ""} onChange={handleChange}
                                           name="termsConditions" value={details.termsConditions ? "" : "on"} />}
                        label={<Typography
                            variant="body2">I have read and accepted the <a href="https://datashop.deutsche-boerse.com/terms-and-conditions">Terms and Conditions to the Historical Data Shop of Deutsche Börse AG</a>.*</Typography>}
                    />
                </Grid>
                <Grid item xs={12}>
                    <FormControlLabel
                        control={<Checkbox checked={details.confirmLegalEntity !== ""} onChange={handleChange}
                                           name="confirmLegalEntity"
                                           value={details.confirmLegalEntity ? "" : "on"} />}
                        label={<Typography
                            variant="body2">{"I hereby confirm that the client is a legal entity.*"}</Typography>}
                    />
                </Grid>
            </Grid>
            <Grid item xs={2}>
                <BlueButton onClick={handleSubmit} disabled={!formIsValid()}>
                    {"Send"}
                </BlueButton>
                <FormHelperText error={!formIsValid()}>{errorMessage}</FormHelperText>
            </Grid>
            <Grid item xs={12}>
                <Typography variant="body2">* Required fields</Typography>
                <Typography
                        variant="body2">** Actual effective date and trial period will be confirmed by our Customer Support after successful setup</Typography>
            </Grid>
        </Grid>
    );
};

export const SampleDataForm = withStyles(styles)(SampleDataFormPure);
